import { PlusIcon } from '@shopify/polaris-icons';
import { Page, TextField, Select, Modal } from '../components/forms';
import { useEffect, useState, useCallback, useRef } from 'react';
import {
  Layout,
  Card,
  Text,
  BlockStack,
  Box,
  InlineStack,
  Button,
  Badge,
  Banner,
  ChoiceList,
  Tabs,
  DataTable,
  EmptyState,
  Spinner,
  ProgressBar,
} from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../App';
import { useRouteSave, localRouteDate } from '../components/routes/useRouteSave';

interface DeliveryRoute {
  id: number;
  name: string | null;
  delivery_date: string;
  zone_id: number | null;
  zone_name: string | null;
  driver_name: string | null;
  vehicle: string | null;
  status: string;
  total_distance_km: number | null;
  estimated_start_time: string | null;
  stop_count: number;
  completed_stops: number;
}

interface Zone {
  id: number;
  name: string;
}

export default function Routes() { const { currentShop } = useAuth(); return <RouteList key={currentShop?.id} />; }
function RouteList() {
  const navigate = useNavigate();
  const { fetchApi, error, loading } = useApi(); const { hasPermission } = useAuth();
  const generation = useRef(0); const statsGeneration = useRef(0);
  const [total, setTotal] = useState(0); const [offset, setOffset] = useState(0);
  const [routes, setRoutes] = useState<DeliveryRoute[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedDate, setSelectedDate] = useState(() => {
    return localRouteDate();
  });
  const [selectedZone, setSelectedZone] = useState('');

  // Create modal
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDate, setNewDate] = useState(() => localRouteDate());
  const [newZones, setNewZones] = useState<string[]>([]);
  const [newStartTime, setNewStartTime] = useState('08:00');
  const mutation = useRouteSave(); const creating = mutation.saving;

  // Dashboard stats
  const [stats, setStats] = useState<{
    today: {
      routes: { total: number; draft: number; planned: number; in_progress: number; completed: number };
      deliveries: { total: number; delivered: number; failed: number };
      unassigned_orders: number;
    };
  } | null>(null);

  const tabs = [
    { id: 'all', content: 'All' },
    { id: 'draft', content: 'Draft', badge: counts.draft?.toString() },
    { id: 'planned', content: 'Planned', badge: counts.planned?.toString() },
    { id: 'in_progress', content: 'In Progress', badge: counts.in_progress?.toString() },
    { id: 'completed', content: 'Completed', badge: counts.completed?.toString() },
    { id: 'cancelled', content: 'Cancelled' },
  ];

  const loadRoutes = useCallback(async () => {
    const version = ++generation.current;
    const status = tabs[selectedTab].id;
    let url = `/routes?status=${status}&offset=${offset}`;
    if (selectedDate) url += `&date=${selectedDate}`;
    if (selectedZone) url += `&zone_id=${selectedZone}`;

    const data = await fetchApi<{
      routes: DeliveryRoute[];
      counts: Record<string, number>;
      zones: Zone[]; pagination: { total: number };
    }>(url);

    if (version !== generation.current) return;
    setTotal(data?.pagination.total || 0);
    if (!data) { setRoutes([]); setCounts({}); return; }
    if (data) {
      setRoutes(data.routes || []);
      setCounts(data.counts || {});
      setZones(data.zones || []);
    }
  }, [fetchApi, selectedTab, selectedDate, selectedZone, offset]);

  const loadStats = useCallback(async () => {
    const version = ++statsGeneration.current;
    const data = await fetchApi<{ today: typeof stats extends null ? never : NonNullable<typeof stats>['today'] }>('/routes/stats/dashboard');
    if (version !== statsGeneration.current) return;
    if (!data) setStats(null);
    if (data && data.today && data.today.routes) {
      setStats(data as typeof stats);
    }
  }, [fetchApi]);

  useEffect(() => {
    loadRoutes(); return () => { generation.current++; };
  }, [loadRoutes]);
  useEffect(() => { loadStats(); return () => { statsGeneration.current++; }; }, [loadStats]);

  const handleCreate = async () => {
    if (!newDate || creating) return;


    const result = await mutation.save('/routes', 'POST', {
          name: newName.trim() || null,
          delivery_date: newDate,
          zone_ids: newZones.map(Number),
          estimated_start_time: newStartTime || null,
      });

      if (result?.success && result.id) {
        setCreateModalOpen(false);
        resetForm();
        navigate(`/routes/${result.id}`);
      }
  };

  const resetForm = () => {
    setNewName('');
    setNewDate(localRouteDate());
    setNewZones([]);
    setNewStartTime('08:00');
  };

  const getStatusBadge = (status: string) => {
    const tones: Record<string, 'info' | 'success' | 'warning' | 'critical' | 'attention'> = {
      draft: 'attention',
      planned: 'info',
      in_progress: 'warning',
      completed: 'success',
      cancelled: 'critical',
    };
    const labels: Record<string, string> = {
      draft: 'Draft',
      planned: 'Planned',
      in_progress: 'In Progress',
      completed: 'Completed',
      cancelled: 'Cancelled',
    };
    return <Badge tone={tones[status] || 'info'}>{labels[status] || status}</Badge>;
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(`${dateStr.slice(0, 10)}T12:00:00`).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatTime = (timeStr: string | null) => {
    if (!timeStr) return '-';
    return timeStr.slice(0, 5);
  };

  const zoneOptions = [
    { label: 'All Zones', value: '' },
    ...zones.map(z => ({ label: z.name, value: String(z.id) })),
  ];

  const tableRows = routes.map((route) => {
    const progress = route.stop_count > 0 ? Math.round((route.completed_stops / route.stop_count) * 100) : 0;
    return [
      <Button variant="plain" onClick={() => navigate(`/routes/${route.id}`)}>
        {route.name || `Route ${route.id}`}
      </Button>,
      formatDate(route.delivery_date),
      route.zone_name || '-',
      route.driver_name || '-',
      formatTime(route.estimated_start_time),
      String(route.stop_count),
      route.status === 'in_progress' || route.status === 'completed' ? (
        <div style={{ width: '80px' }}>
          <ProgressBar progress={progress} size="small" tone={progress === 100 ? 'success' : 'primary'} />
          <Text as="span" variant="bodySm" tone="subdued">{progress}%</Text>
        </div>
      ) : (
        '-'
      ),
      route.total_distance_km !== null ? `${route.total_distance_km} km (straight line)` : '-',
      getStatusBadge(route.status),
      <Button size="slim" onClick={() => navigate(`/routes/${route.id}`)}>
        View
      </Button>,
    ];
  });

  return (
    <Page
      title="Delivery Routes"
      subtitle="Plan and manage delivery routes"
      primaryAction={{
        icon: PlusIcon, content: 'Create Route', disabled: !hasPermission('routes.create'),
        onAction: () => setCreateModalOpen(true),
      }}
      secondaryActions={[
        {
          content: 'Build Route',
          onAction: () => navigate('/routes/build'),
        },
      ]}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        {/* Stats Cards */}
        {stats?.today?.routes && (
          <Layout.Section>
            <InlineStack gap="400" wrap={false}>
              <Card>
                <BlockStack gap="200">
                  <Text as="span" variant="bodySm" tone="subdued">Today's Routes</Text>
                  <Text as="p" variant="heading2xl">{stats.today.routes.total}</Text>
                  <InlineStack gap="200">
                    {stats.today.routes.in_progress > 0 && (
                      <Badge tone="warning">{`${stats.today.routes.in_progress} active`}</Badge>
                    )}
                    {stats.today.routes.completed > 0 && (
                      <Badge tone="success">{`${stats.today.routes.completed} done`}</Badge>
                    )}
                  </InlineStack>
                </BlockStack>
              </Card>
              <Card>
                <BlockStack gap="200">
                  <Text as="span" variant="bodySm" tone="subdued">Today's Deliveries</Text>
                  <Text as="p" variant="heading2xl">{stats.today.deliveries?.delivered || 0}/{stats.today.deliveries?.total || 0}</Text>
                  {(stats.today.deliveries?.failed || 0) > 0 && (
                    <Badge tone="critical">{`${stats.today.deliveries.failed} failed`}</Badge>
                  )}
                </BlockStack>
              </Card>
              <Card>
                <BlockStack gap="200">
                  <Text as="span" variant="bodySm" tone="subdued">Unassigned Orders</Text>
                  <Text as="p" variant="heading2xl">{stats.today.unassigned_orders || 0}</Text>
                  {(stats.today.unassigned_orders || 0) > 0 && (
                    <Button size="slim" onClick={() => navigate('/routes/build')}>
                      Build Route
                    </Button>
                  )}
                </BlockStack>
              </Card>
            </InlineStack>
          </Layout.Section>
        )}

        {/* Filters */}
        <Layout.Section>
          <Card>
            <InlineStack gap="400" align="start">
              <div style={{ width: '160px' }}>
                <TextField
                  label="Date"
                  type="date"
                  value={selectedDate}
                  onChange={v => { setSelectedDate(v); setOffset(0); }}
                  autoComplete="off"
                  labelHidden
                />
              </div>
              <div style={{ width: '180px' }}>
                <Select
                  label="Zone"
                  labelHidden
                  options={zoneOptions}
                  value={selectedZone}
                  onChange={v => { setSelectedZone(v); setOffset(0); }}
                />
              </div>
              <Button onClick={loadRoutes}>Refresh</Button>
              <Button onClick={() => {
                setSelectedDate('');
                setSelectedZone('');
              }}>Clear Filters</Button>
            </InlineStack>
          </Card>
        </Layout.Section>

        {/* Routes List */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Tabs
                tabs={tabs.map(t => ({
                  id: t.id,
                  content: t.badge ? `${t.content} (${t.badge})` : t.content,
                }))}
                selected={selectedTab}
                onSelect={v => { setSelectedTab(v); setOffset(0); }}
              />

              {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                  <Spinner />
                </div>
              ) : routes.length === 0 ? (
                <EmptyState
                  heading="No routes found"
                  action={{
                    icon: PlusIcon, content: 'Create Route', disabled: !hasPermission('routes.create'),
                    onAction: () => setCreateModalOpen(true),
                  }}
                  image=""
                >
                  <p>Create a delivery route to start planning your deliveries.</p>
                </EmptyState>
              ) : (
                <DataTable
                  columnContentTypes={[
                    'text', 'text', 'text', 'text', 'text',
                    'numeric', 'text', 'text', 'text', 'text',
                  ]}
                  headings={[
                    'Route', 'Date', 'Zone', 'Driver', 'Start',
                    'Stops', 'Progress', 'Straight-line estimate', 'Status', 'Actions',
                  ]}
                  rows={tableRows}
                />
              )}
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>

      <Box paddingBlockStart="400">
        <InlineStack align="space-between">
          <Text as="p">{total} matching routes</Text>
          <InlineStack gap="200">
            <Button disabled={loading || offset === 0} onClick={() => setOffset(n => Math.max(0, n - 100))}>Previous</Button>
            <Button disabled={loading || offset + 100 >= total} onClick={() => setOffset(n => n + 100)}>Next</Button>
          </InlineStack>
        </InlineStack>
      </Box>
      {/* Create Modal */}
      <Modal
        open={createModalOpen}
        onClose={() => {
          setCreateModalOpen(false);

        }}
        title="Create Delivery Route"
        primaryAction={{
          icon: PlusIcon, content: 'Create',
          onAction: handleCreate,
          loading: creating,
          disabled: !newDate || creating,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setCreateModalOpen(false);

            },
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {mutation.error && <Banner tone="critical">{mutation.error} Retry this save, or close the form and check the route list before starting another.</Banner>}
            <Text as="p">Choose a van and driver from the fleet booking panel after saving.</Text>
            <TextField
              disabled={mutation.locked}
              label="Route Name"
              requiredIndicator value={newName}
              onChange={setNewName}
              autoComplete="off"
              placeholder="e.g., Morning Run - North Zone"
              helpText="Optional name for this route"
            />

            <TextField
              disabled={mutation.locked}
              label="Delivery Date"
              type="date"
              requiredIndicator value={newDate}
              onChange={setNewDate}
              autoComplete="off"

            />

            <ChoiceList title="Delivery zones" allowMultiple
              disabled={mutation.locked}
              choices={zones.map(z => ({ label: z.name, value: String(z.id) }))}
              selected={newZones} onChange={setNewZones} />
            <Text as="p" tone="subdued">Choose the zones this route can serve. Leave empty to include all zones.</Text>

            <TextField
              disabled={mutation.locked}
              label="Estimated Start Time"
              type="time"
              value={newStartTime}
              onChange={setNewStartTime}
              autoComplete="off"
            />
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
